import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import api from '../services/api';

const ROLE_LABELS = {
  admin: 'Administrátor',
  manager: 'Manažer',
  editor: 'Editor',
  viewer: 'Uživatel',
};

function UserDetail({ userId, openTab, closeTab, activeTab }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'viewer',
    is_active: true,
    password: '',
  });

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.getUser(userId);
      const data = result.data || {};
      setUser(data);
      setForm({
        email: data.email || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        role: data.role || 'viewer',
        is_active: !!data.is_active,
        password: '',
      });
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setError(err.message || 'Nepodařilo se načíst uživatele.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role,
        is_active: form.is_active ? 1 : 0,
      };
      if (form.password) payload.password = form.password;
      await api.updateUser(userId, payload);
      setMessage('Uloženo.');
      setForm((prev) => ({ ...prev, password: '' }));
      await fetchUser();
      window.dispatchEvent(new CustomEvent('users:refresh'));
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Uložení se nepodařilo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="px-4 py-4">
        <div className="text-muted">Načítám…</div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid className="px-4 py-4">
        <Alert variant="danger">{error || 'Uživatel nenalezen.'}</Alert>
      </Container>
    );
  }

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;

  return (
    <Container fluid className="px-4 py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">{fullName}</h4>
          <div className="text-muted small">
            <Badge bg="secondary" className="me-1">{ROLE_LABELS[user.role] || user.role}</Badge>
            {user.is_active
              ? <Badge bg="success">Aktivní</Badge>
              : <Badge bg="secondary">Neaktivní</Badge>}
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <div className="card mb-3">
        <div className="card-body">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Login (email)</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Jméno</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Příjmení</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select value={form.role} onChange={(e) => handleChange('role', e.target.value)}>
                    <option value="admin">Administrátor</option>
                    <option value="manager">Manažer</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Uživatel</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Nové heslo (volitelné)</Form.Label>
                  <Form.Control
                    type="password"
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    autoComplete="new-password"
                    placeholder="Ponechat prázdné = bez změny"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3 pb-2">
                  <Form.Check
                    type="switch"
                    id="user-active-switch"
                    label="Aktivní"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              {closeTab && activeTab && (
                <Button variant="outline-secondary" type="button" onClick={() => closeTab(activeTab)}>
                  Zavřít
                </Button>
              )}
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Ukládám…' : 'Uložit'}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">Skupiny</h6>
          {Array.isArray(user.groups) && user.groups.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {user.groups.map((g) => (
                <Badge key={g} bg="light" text="dark" className="border">{g}</Badge>
              ))}
            </div>
          ) : (
            <div className="text-muted small">Bez skupin.</div>
          )}
        </div>
      </div>
    </Container>
  );
}

export default UserDetail;
